import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Button, Grid, TextField, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Dialog, DialogTitle, DialogContent,
  DialogActions, FormControl, InputLabel, Select, MenuItem, Chip, IconButton,
  Tooltip, InputAdornment, Alert
} from '@mui/material';
import {
  Add as AddIcon, Search as SearchIcon, Refresh as RefreshIcon, LibraryBooks,
  AssignmentReturn, Delete as DeleteIcon, Edit as EditIcon,
  FileDownload as ImportIcon, Warning as WarningIcon, MonetizationOn as FineIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { libraryAPI, studentAPI } from '../services/api';
import ExcelImport from '../components/ExcelImport';

const STATUS_COLORS = {
  issued: 'warning', returned: 'success', overdue: 'error',
  lost: 'default', missing: 'secondary', left_school: 'info'
};

const STATUS_OPTIONS = [
  { value: 'returned', label: 'Returned' },
  { value: 'lost', label: 'Lost' },
  { value: 'missing', label: 'Missing' },
  { value: 'left_school', label: 'Student/Staff Left School' },
];

const Library = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [books, setBooks] = useState([]);
  const [issues, setIssues] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [issueSearchTerm, setIssueSearchTerm] = useState('');
  const [issueTabSearch, setIssueTabSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Dialog States
  const [openBookDialog, setOpenBookDialog] = useState(false);
  const [editingBook, setEditingBook] = useState(null);
  const [openIssueDialog, setOpenIssueDialog] = useState(false);
  const [openStatusDialog, setOpenStatusDialog] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState(null);

  // Status change form
  const [statusForm, setStatusForm] = useState({ status: 'returned', remarks: '', fineAmount: 0 });

  // Forms
  const [bookForm, setBookForm] = useState({
    title: '', author: '', isbn: '', publisher: '', category: '', copies: 1, rackLocation: '', distributor: '', price: ''
  });

  const [issueForm, setIssueForm] = useState({
    bookId: '', userId: '', dueDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  });

  const [userSearch, setUserSearch] = useState('');
  const [userResults, setUserResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedBookToIssue, setSelectedBookToIssue] = useState(null);
  const [openImport, setOpenImport] = useState(false);

  useEffect(() => {
    if (activeTab === 0) fetchBooks();
    else fetchIssues();
  }, [activeTab]);

  const fetchBooks = async () => {
    setLoading(true);
    try {
      const res = await libraryAPI.getBooks({ search: searchTerm });
      setBooks(res.data.data);
    } catch (error) { toast.error('Failed to fetch books'); }
    finally { setLoading(false); }
  };

  const fetchIssues = async () => {
    setLoading(true);
    try {
      const res = await libraryAPI.getIssues();
      setIssues(res.data.data || []);
    } catch (error) { toast.error('Failed to fetch issue records'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    const t = setTimeout(() => { if (activeTab === 0) fetchBooks(); }, 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const filteredBooksForIssue = books.filter(b => {
    if (!issueSearchTerm) return true;
    const term = issueSearchTerm.toLowerCase();
    return b.title?.toLowerCase().includes(term) || b.author?.toLowerCase().includes(term) || b.isbn?.toLowerCase().includes(term);
  });

  const handleCreateOrUpdateBook = async () => {
    try {
      if (editingBook) { await libraryAPI.updateBook(editingBook.id, bookForm); toast.success('Book updated'); }
      else { await libraryAPI.createBook(bookForm); toast.success('Book created'); }
      setOpenBookDialog(false); setEditingBook(null); fetchBooks();
    } catch (error) { toast.error('Operation failed'); }
  };

  const handleOpenBookDialog = (book = null) => {
    if (book) {
      setEditingBook(book);
      setBookForm({ title: book.title, author: book.author, isbn: book.isbn || '', publisher: book.publisher || '',
        category: book.category || '', copies: book.copies, rackLocation: book.rackLocation || '', distributor: book.distributor || '', price: book.price || '' });
    } else {
      setEditingBook(null);
      setBookForm({ title: '', author: '', isbn: '', publisher: '', category: '', copies: 1, rackLocation: '', distributor: '', price: '' });
    }
    setOpenBookDialog(true);
  };

  const handleDeleteBook = async (id) => {
    if (window.confirm('Are you sure?')) {
      try { await libraryAPI.deleteBook(id); toast.success('Book deleted'); fetchBooks(); }
      catch (error) { toast.error('Failed to delete'); }
    }
  };

  const handleSearchUser = async (searchValue) => {
    if (!searchValue || searchValue.length < 2) { setUserResults([]); return; }
    try {
      const res = await studentAPI.getAll({ search: searchValue });
      const results = res.data.data || [];
      const term = searchValue.toLowerCase();
      const filtered = results.filter(u => {
        const firstName = u.user?.firstName?.toLowerCase() || '';
        const lastName = u.user?.lastName?.toLowerCase() || '';
        const admNo = u.admissionNumber?.toLowerCase() || '';
        return firstName.includes(term) || lastName.includes(term) || admNo.includes(term);
      });
      setUserResults(filtered);
    } catch (error) { console.error(error); }
  };

  useEffect(() => {
    const t = setTimeout(() => { handleSearchUser(userSearch); }, 300);
    return () => clearTimeout(t);
  }, [userSearch]);

  const handleIssueBook = async () => {
    if (!selectedBookToIssue || !selectedUser) { toast.error('Please select both book and user'); return; }
    try {
      await libraryAPI.issueBook({ bookId: selectedBookToIssue.id, userId: selectedUser.userId, dueDate: issueForm.dueDate });
      toast.success('Book issued successfully');
      setOpenIssueDialog(false); setSelectedBookToIssue(null); setSelectedUser(null);
      fetchIssues();
    } catch (error) { toast.error(error.response?.data?.message || 'Issue failed'); }
  };

  // Enhanced return/status change
  const handleOpenStatusDialog = (issue) => {
    setSelectedIssue(issue);
    setStatusForm({ status: 'returned', remarks: '', fineAmount: 0 });
    setOpenStatusDialog(true);
  };

  const handleStatusChange = async () => {
    if (!selectedIssue) return;
    try {
      await libraryAPI.returnBook(selectedIssue.id, statusForm);
      toast.success(`Book status updated to "${statusForm.status}"`);
      setOpenStatusDialog(false); fetchIssues();
    } catch (error) { toast.error(error.response?.data?.message || 'Update failed'); }
  };

  const handleImportBooks = async (data) => {
    try {
      const res = await libraryAPI.bulkImport({ books: data });
      toast.success(res.data.message); fetchBooks();
    } catch (error) { throw new Error(error.response?.data?.message || 'Import failed'); }
  };

  // Check overdue
  const isOverdue = (issue) => {
    if (issue.status !== 'issued') return false;
    return new Date(issue.dueDate) < new Date();
  };

  // Stats
  const issuedCount = issues.filter(i => i.status === 'issued').length;
  const overdueCount = issues.filter(i => isOverdue(i)).length;
  const returnedCount = issues.filter(i => i.status === 'returned').length;
  const lostCount = issues.filter(i => i.status === 'lost' || i.status === 'missing').length;

  const renderBooksTab = () => (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <TextField placeholder="Search books..." size="small" value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)} sx={{ minWidth: 250 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} />
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" startIcon={<ImportIcon />} onClick={() => setOpenImport(true)}>Import Excel</Button>
          <Button startIcon={<AddIcon />} variant="contained" onClick={() => handleOpenBookDialog()}>Add Book</Button>
        </Box>
      </Box>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 700 }}>Title</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Author</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>ISBN</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Avail/Total</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Rack</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Price</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {books.map(book => (
              <TableRow key={book.id} hover>
                <TableCell>{book.title}</TableCell>
                <TableCell>{book.author}</TableCell>
                <TableCell>{book.category}</TableCell>
                <TableCell>{book.isbn}</TableCell>
                <TableCell>
                  <Chip label={`${book.availableCopies}/${book.copies}`}
                    color={book.availableCopies > 0 ? 'success' : 'error'} size="small" />
                </TableCell>
                <TableCell>{book.rackLocation}</TableCell>
                <TableCell>{book.price ? `₹${book.price}` : '-'}</TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => handleOpenBookDialog(book)}><EditIcon /></IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDeleteBook(book.id)}><DeleteIcon /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <ExcelImport open={openImport} onClose={() => setOpenImport(false)} onImport={handleImportBooks}
        title="Import Books" template={{ title: 'Physics', author: 'H.C. Verma', isbn: '978-81-7709-187-8',
          publisher: 'Bharti Bhawan', category: 'Science', copies: 5, rackLocation: 'A1', distributor: 'ABC', price: 500 }} />
    </Box>
  );

  const renderIssuesTab = () => {
    let filteredIssues = issues.filter(issue => {
      if (!issueTabSearch) return true;
      const term = issueTabSearch.toLowerCase();
      return (issue.book?.title?.toLowerCase() || '').includes(term) ||
        (issue.member?.user?.firstName?.toLowerCase() || '').includes(term) ||
        (issue.member?.user?.lastName?.toLowerCase() || '').includes(term);
    });

    if (statusFilter !== 'all') {
      if (statusFilter === 'overdue') filteredIssues = filteredIssues.filter(i => isOverdue(i));
      else filteredIssues = filteredIssues.filter(i => i.status === statusFilter);
    }

    const sortedIssues = [...filteredIssues].sort((a, b) => {
      if (a.status === 'issued' && b.status !== 'issued') return -1;
      if (a.status !== 'issued' && b.status === 'issued') return 1;
      return new Date(b.createdAt) - new Date(a.createdAt);
    });

    return (
      <Box>
        {/* Stats */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {[
            { label: 'Currently Issued', count: issuedCount, color: '#ff9800' },
            { label: 'Overdue', count: overdueCount, color: '#f44336' },
            { label: 'Returned', count: returnedCount, color: '#4caf50' },
            { label: 'Lost/Missing', count: lostCount, color: '#9e9e9e' },
          ].map(s => (
            <Grid item xs={6} sm={3} key={s.label}>
              <Paper sx={{ p: 1.5, textAlign: 'center', borderLeft: `4px solid ${s.color}` }}>
                <Typography variant="h5" fontWeight={700}>{s.count}</Typography>
                <Typography variant="caption" color="text.secondary">{s.label}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button startIcon={<AddIcon />} variant="contained" onClick={() => setOpenIssueDialog(true)}>Issue New Book</Button>
          <TextField size="small" placeholder="Search by book or student..." value={issueTabSearch}
            onChange={e => setIssueTabSearch(e.target.value)} sx={{ minWidth: 220 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} />
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} label="Status" onChange={e => setStatusFilter(e.target.value)}>
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="issued">Issued</MenuItem>
              <MenuItem value="overdue">Overdue</MenuItem>
              <MenuItem value="returned">Returned</MenuItem>
              <MenuItem value="lost">Lost</MenuItem>
              <MenuItem value="missing">Missing</MenuItem>
              <MenuItem value="left_school">Left School</MenuItem>
            </Select>
          </FormControl>
          <Button startIcon={<RefreshIcon />} onClick={fetchIssues}>Refresh</Button>
        </Box>

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 700 }}>Book</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Issued To</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Issue Date</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Due Date</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Return Date</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Fine</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Remarks</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedIssues.map(issue => (
                <TableRow key={issue.id} hover sx={{
                  bgcolor: isOverdue(issue) ? '#fff3e0' : 'inherit'
                }}>
                  <TableCell>{issue.book?.title}</TableCell>
                  <TableCell>{issue.member?.user?.firstName} {issue.member?.user?.lastName}</TableCell>
                  <TableCell>{issue.issueDate}</TableCell>
                  <TableCell sx={{ color: isOverdue(issue) ? '#f44336' : 'inherit', fontWeight: isOverdue(issue) ? 700 : 400 }}>
                    {issue.dueDate} {isOverdue(issue) && <WarningIcon sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'middle' }} />}
                  </TableCell>
                  <TableCell>{issue.returnDate || '-'}</TableCell>
                  <TableCell>
                    <Chip label={issue.status?.replace('_', ' ')} color={STATUS_COLORS[issue.status] || 'default'} size="small" />
                  </TableCell>
                  <TableCell>{issue.fineAmount > 0 ? `₹${issue.fineAmount}` : '-'}</TableCell>
                  <TableCell sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <Tooltip title={issue.remarks || ''}><span>{issue.remarks || '-'}</span></Tooltip>
                  </TableCell>
                  <TableCell>
                    {issue.status === 'issued' && (
                      <Button size="small" variant="outlined" startIcon={<AssignmentReturn />}
                        onClick={() => handleOpenStatusDialog(issue)}>
                        Update
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {sortedIssues.length === 0 && (
                <TableRow><TableCell colSpan={9} align="center">No records found</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>Library Management</Typography>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
          <Tab label="Books Inventory" icon={<LibraryBooks />} iconPosition="start" />
          <Tab label="Issued / Returns" icon={<AssignmentReturn />} iconPosition="start" />
        </Tabs>
      </Paper>

      {activeTab === 0 && renderBooksTab()}
      {activeTab === 1 && renderIssuesTab()}

      {/* Book Dialog */}
      <Dialog open={openBookDialog} onClose={() => setOpenBookDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingBook ? 'Edit Book' : 'Add New Book'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Title" value={bookForm.title} onChange={e => setBookForm({ ...bookForm, title: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Author" value={bookForm.author} onChange={e => setBookForm({ ...bookForm, author: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="ISBN" value={bookForm.isbn} onChange={e => setBookForm({ ...bookForm, isbn: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Publisher" value={bookForm.publisher} onChange={e => setBookForm({ ...bookForm, publisher: e.target.value })} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Category" value={bookForm.category} onChange={e => setBookForm({ ...bookForm, category: e.target.value })} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Total Copies" type="number" value={bookForm.copies} onChange={e => setBookForm({ ...bookForm, copies: e.target.value })} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Rack Number" value={bookForm.rackLocation} onChange={e => setBookForm({ ...bookForm, rackLocation: e.target.value })} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Distributor" value={bookForm.distributor} onChange={e => setBookForm({ ...bookForm, distributor: e.target.value })} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Price" type="number" value={bookForm.price} onChange={e => setBookForm({ ...bookForm, price: e.target.value })} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenBookDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateOrUpdateBook}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Issue Dialog */}
      <Dialog open={openIssueDialog} onClose={() => setOpenIssueDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Issue Book</DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle2">1. Select Book</Typography>
          <TextField fullWidth size="small" placeholder="Search books..." value={issueSearchTerm}
            onChange={e => setIssueSearchTerm(e.target.value)} sx={{ mb: 1, mt: 1 }} />
          <FormControl fullWidth margin="dense">
            <InputLabel>Select Book</InputLabel>
            <Select label="Select Book" value={selectedBookToIssue ? selectedBookToIssue.id : ''}>
              {filteredBooksForIssue.filter(b => b.availableCopies > 0).map(b => (
                <MenuItem key={b.id} value={b.id} onClick={() => setSelectedBookToIssue(b)}>
                  {b.title} - {b.author} (Avail: {b.availableCopies})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {selectedBookToIssue && <Chip label={`Selected: ${selectedBookToIssue.title}`} onDelete={() => setSelectedBookToIssue(null)} sx={{ mt: 1 }} color="primary" />}

          <Typography variant="subtitle2" sx={{ mt: 2 }}>2. Select Student</Typography>
          <TextField fullWidth size="small" placeholder="Type student name (min 2 chars)..." value={userSearch}
            onChange={e => setUserSearch(e.target.value)} helperText="Start typing to search..." />
          {userResults.length > 0 && (
            <Paper variant="outlined" sx={{ maxHeight: 150, overflow: 'auto' }}>
              {userResults.map(u => (
                <MenuItem key={u.id} onClick={() => { setSelectedUser(u); setUserResults([]); }}>
                  {u.user?.firstName} {u.user?.lastName} ({u.admissionNumber})
                </MenuItem>
              ))}
            </Paper>
          )}
          {selectedUser && <Chip label={`Selected: ${selectedUser.user?.firstName} ${selectedUser.user?.lastName}`} onDelete={() => setSelectedUser(null)} sx={{ mt: 1 }} />}

          <TextField fullWidth label="Due Date" type="date" margin="normal" InputLabelProps={{ shrink: true }}
            value={issueForm.dueDate} onChange={e => setIssueForm({ ...issueForm, dueDate: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenIssueDialog(false)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleIssueBook} disabled={!selectedBookToIssue || !selectedUser}>Issue</Button>
        </DialogActions>
      </Dialog>

      {/* Status Update Dialog */}
      <Dialog open={openStatusDialog} onClose={() => setOpenStatusDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Update Book Status</DialogTitle>
        <DialogContent dividers>
          {selectedIssue && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>{selectedIssue.book?.title}</strong> issued to <strong>{selectedIssue.member?.user?.firstName} {selectedIssue.member?.user?.lastName}</strong>
              {isOverdue(selectedIssue) && <Chip label="OVERDUE" color="error" size="small" sx={{ ml: 1 }} />}
            </Alert>
          )}
          <FormControl fullWidth margin="normal">
            <InputLabel>New Status</InputLabel>
            <Select value={statusForm.status} label="New Status" onChange={e => setStatusForm({ ...statusForm, status: e.target.value })}>
              {STATUS_OPTIONS.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField fullWidth label="Fine Amount (₹)" type="number" margin="normal"
            value={statusForm.fineAmount} onChange={e => setStatusForm({ ...statusForm, fineAmount: parseFloat(e.target.value) || 0 })}
            InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
          <TextField fullWidth label="Remarks" multiline rows={2} margin="normal"
            value={statusForm.remarks} onChange={e => setStatusForm({ ...statusForm, remarks: e.target.value })}
            placeholder="e.g. Book damaged, Student left school, Paid fine..." />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenStatusDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleStatusChange}
            color={statusForm.status === 'returned' ? 'success' : 'warning'}>
            Update Status
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Library;
